/**
 * One editable address of the resolve-label-error flow. Seeded from the prefetch and edited in
 * place, so what the merchant sees is already the payload: the rates and approve calls send
 * addressRequest as it stands. One instance per address; the validate-shipping-details screen
 * takes ownership once its address cards are filled in.
 *
 * The phone is held as a PhoneValue rather than a string: the field shows a calling code and a
 * national part, and neither survives being written back as one string. Holding it here also means
 * a half-typed number outlives the card being collapsed. It is independent of the address country -
 * the customer's phone is often not from the country the parcel goes to.
 */
import { makeAutoObservable } from "mobx";
import { IOption } from "../../../../../components/inputs/select-dropdown/select-dropdown";
import { IShippingDetailsAddress, IShippingDetailsCountry } from "../../rma-types";
import PhoneValue from "./phone-value";

/** The shape the form binds to, so a missing address still gives every field something to read. */
const EMPTY_ADDRESS: IShippingDetailsAddress = {
    name: null,
    email: null,
    company: null,
    street1: null,
    street2: null,
    city: null,
    state: null,
    state_name: null,
    country: null,
    zip: null,
    phone: null,
    suburb: null,
};

class AddressStore {
    private _address: IShippingDetailsAddress;
    private _countries: IShippingDetailsCountry[];
    private _phone: PhoneValue;

    constructor(address: IShippingDetailsAddress | null, countries: IShippingDetailsCountry[] = []) {
        this._address = { ...EMPTY_ADDRESS, ...address };
        this._countries = countries;
        this._phone = new PhoneValue(this._address.phone ?? undefined);
        makeAutoObservable(this, {}, { autoBind: true });
    }

    public get name(): string | null { return this._address.name; }

    public get email(): string | null { return this._address.email; }

    public get company(): string | null { return this._address.company; }

    public get street1(): string | null { return this._address.street1; }

    public get street2(): string | null { return this._address.street2; }

    public get city(): string | null { return this._address.city; }

    public get state(): string | null { return this._address.state; }

    public get stateName(): string | null { return this._address.state_name; }

    public get country(): string | null { return this._address.country; }

    public get zip(): string | null { return this._address.zip; }

    /** The phone as the field edits it: a calling code and a national part, not yet a string. */
    public get phone(): PhoneValue { return this._phone; }

    /** The countries the prefetch offers, as the address dropdown takes them. */
    public get countryOptions(): IOption[] {
        return this._countries.map(({ name, code }) => ({ label: name, value: code }));
    }

    /** The same list as the codes they dial; ones that cannot be dialled at all are dropped. */
    public get callingCodeOptions(): IOption[] {
        return this._countries.flatMap(({ name, code }) => {
            const callingCode = PhoneValue.callingCodeOf(code);

            return callingCode ? [{ value: code, label: `+${callingCode}`, description: name }] : [];
        });
    }

    /** The one the address currently names, or null while it names none of them. */
    public get selectedCountry(): IOption | null {
        return this.countryOptions.find((option) => option.value === this.countryCode) ?? null;
    }

    /**
     * The address country as ISO-2. The backend stores whichever of the two the order carried, so
     * match on both: a country name means nothing to the phone field.
     */
    public get countryCode(): string | undefined {
        return this._countries.find(
            ({ code, name }) => code === this._address.country || name === this._address.country,
        )?.code;
    }

    public setName(name: string | null): void { this._address.name = name; }

    public setEmail(email: string | null): void { this._address.email = email; }

    public setCompany(company: string | null): void { this._address.company = company; }

    public setStreet1(street1: string | null): void { this._address.street1 = street1; }

    public setStreet2(street2: string | null): void { this._address.street2 = street2; }

    public setCity(city: string | null): void { this._address.city = city; }

    public setState(state: string | null): void { this._address.state = state; }

    public setStateName(stateName: string | null): void { this._address.state_name = stateName; }

    public setZip(zip: string | null): void { this._address.zip = zip; }

    public setCountry(country: string | null): void { this._address.country = country; }

    /** What the backend expects for customer_address and return_address. */
    public get addressRequest(): IShippingDetailsAddress {
        return { ...this._address, phone: this._phone.value };
    }
}

export default AddressStore;
